import { FC, useMemo } from 'react';
import { getTodayMoment, standardDateFormat } from '@constants';
import { Bar } from 'react-chartjs-2';
import { conditionallyRenderElement, formatChartDateRange } from 'utils';
import { Box } from '@mui/material';
import { transformBarChartData } from 'IntegrationModule/utils/barChart';
import { defaultChartData } from '@constants/chart';
import sumBy from 'lodash/sumBy';
import { useTeamMemberStatisticsQuery } from 'TeamModule/query/hooks/useTeamMemberStatisticsQuery';
import {
  MemberStats,
  memberStatsConfigs,
  memberStatsDataTypes,
} from 'TeamModule/constants/memberStats';
import { OrderedChartData } from 'types/barChart';
import { format, sub, add } from 'date-fns';

const today = getTodayMoment();
const teamMemberStatisticsStartDate = format(sub(today, { months: 1 }), standardDateFormat);

const options = {
  responsive: true,
  scales: {
    x: {
      stacked: false,
    },
    y: {
      stacked: false,
      ticks: {
        stepSize: 1,
        beginAtZero: true,
      },
      title: {
        display: true,
        text: 'Number of Members',
      },
    },
  },
};

export const TeamMemberStatsContainer: FC<{
  teamId: number;
}> = ({ teamId }) => {
  const { teamMemberStatisticsData, teamMemberStatisticsError, isLoadingTeamMemberStatistics } =
    useTeamMemberStatisticsQuery({
      team_id: teamId,
      search_start_date: teamMemberStatisticsStartDate,
      interval: 7,
    });

  const { chartData } = useMemo(() => {
    if (teamMemberStatisticsData) {
      const memberStatisticsHash = teamMemberStatisticsData.employee_statistics;

      const allMemberStatisticsArray = Object.values(memberStatisticsHash);

      // get x-axis labels from the dataType statistics
      // Member Statistic always has WorkPlansCounts
      const dates = Object.keys(allMemberStatisticsArray[0][MemberStats.WorkPlansCounts]);

      const orderedData: OrderedChartData = [];

      dates.forEach((date) => {
        const bars = memberStatsDataTypes.map((dataType) => {
          const memberCount = sumBy(
            // An array of statistics data object for each member
            allMemberStatisticsArray,
            // For object, each data type, each date, if count is greater than 0, return 1, else 0
            (statsDataType) => (statsDataType[dataType][date] > 0 ? 1 : 0),
          );

          return {
            ...memberStatsConfigs[dataType],
            value: memberCount,
          };
        });
        // get the end date for the x-axis label
        const endDate = format(add(new Date(date), { days: 6 }), standardDateFormat);

        orderedData.push({
          xAxisLabel: formatChartDateRange({ startDate: date, endDate }), // match the x-axis label with the utilization chart
          bars,
        });
      });

      const chartData = transformBarChartData({
        orderedChartData: orderedData,
      });

      return { chartData };
    }

    return { chartData: defaultChartData };
  }, [teamMemberStatisticsData]);

  return (
    <Box>
      <h3>Work Plans, Tasks and Timesheets Counts in previous 30 days</h3>
      {conditionallyRenderElement({
        isLoading: isLoadingTeamMemberStatistics,
        error: teamMemberStatisticsError,
        content: <Bar data={chartData} options={options} />,
      })}
    </Box>
  );
};
